import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { toast } from 'sonner';
import { FileText, Download, Search, User, Trash2, Eye, UserPlus } from 'lucide-react';
import NewClientForm from '../components/NewClientForm';
import { DelegaGeneraleCliente, ElencoDelegheGenerali } from '../components/DelegaGenerale';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Delega = () => {
  const token = localStorage.getItem('token');
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newClientOpen, setNewClientOpen] = useState(false);
  const [delegaList, setDelegaList] = useState([]);
  const [delegaPage, setDelegaPage] = useState(1);
  const DELEGA_PER_PAGE = 30;

  // Luoghi recenti persistenti in localStorage
  const getSavedLuoghi = () => {
    try {
      return JSON.parse(localStorage.getItem('delega_luoghi') || '{}');
    } catch { return {}; }
  };
  const saveLuoghi = (luoghi) => {
    localStorage.setItem('delega_luoghi', JSON.stringify(luoghi));
  };

  const savedLuoghi = getSavedLuoghi();

  const [formData, setFormData] = useState({
    nome_completo: '',
    nato_a: '',
    nato_il: '',
    residente_a: '',
    via: '',
    codice_fiscale: '',
    delegato_nome: '',
    delegato_cognome: '',
    questura: false,
    questura_luogo: savedLuoghi.questura_luogo || 'Prato',
    prefettura: false,
    prefettura_luogo: savedLuoghi.prefettura_luogo || '',
    comune: false,
    comune_luogo: savedLuoghi.comune_luogo || '',
    agenzia_entrate: false,
    agenzia_entrate_luogo: savedLuoghi.agenzia_entrate_luogo || '',
    altro: false,
    altro_descrizione: '',
    operazione: '',
    luogo_data: `Prato, ${new Date().toLocaleDateString('it-IT')}`,
  });

  // Load clients and deleghe
  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } });
      setClients(res.data);
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    fetchClients();
    loadDeleghe();
  }, [token, fetchClients]);

  // Refresh clienti quando l'utente torna sulla tab (es. dopo aver creato un cliente in un'altra tab)
  useEffect(() => {
    const onFocus = () => fetchClients();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchClients]);

  const loadDeleghe = async () => {
    try {
      const res = await axios.get(`${API}/deleghe`, { headers: { Authorization: `Bearer ${token}` } });
      setDelegaList(res.data);
    } catch (e) { console.error(e); }
  };

  const filteredClients = searchQuery.length < 2 ? [] : clients.filter(client => {
    const searchable = [
      client.first_name,
      client.last_name,
      client.company_name,
      client.business_name,
      client.ragione_sociale,
      client.fiscal_code,
      client.codice_fiscale,
      client.piva,
      client.client_code,
    ].filter(Boolean).join(' ').toLowerCase();
    const tokens = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.every(t => searchable.includes(t));
  });

  const selectClient = (client) => {
    setSelectedClient(client);
    setSearchQuery('');

    const fullName = client.client_type === 'azienda'
      ? client.company_name
      : `${client.last_name || ''} ${client.first_name || ''}`.trim();

    let birthPlace = '';
    if (client.birth_abroad) {
      birthPlace = client.birth_country || '';
    } else {
      birthPlace = client.birth_comune || '';
    }

    let birthDate = '';
    if (client.birth_date) {
      try {
        const bd = new Date(client.birth_date);
        birthDate = bd.toLocaleDateString('it-IT');
      } catch (e) {}
    }

    // Via + numero civico
    let via = client.address || '';
    if (client.address_number) {
      via = `${via} ${client.address_number}`.trim();
    }

    // Residenza con provincia
    let residence = client.city || '';
    if (client.provincia) {
      residence = `${residence} (${client.provincia})`;
    }

    setFormData(prev => ({
      ...prev,
      nome_completo: fullName,
      nato_a: birthPlace,
      nato_il: birthDate,
      residente_a: residence,
      via: via,
      codice_fiscale: client.fiscal_code || '',
      luogo_data: `Prato, ${new Date().toLocaleDateString('it-IT')}`
    }));
  };

  const generatePDF = async () => {
    if (!selectedClient) {
      toast.error('Seleziona un cliente');
      return;
    }
    if (!formData.nome_completo || !formData.codice_fiscale) {
      toast.error('Compila i campi obbligatori');
      return;
    }

    // Salva i luoghi recenti in localStorage
    saveLuoghi({
      questura_luogo: formData.questura_luogo,
      prefettura_luogo: formData.prefettura_luogo,
      comune_luogo: formData.comune_luogo,
      agenzia_entrate_luogo: formData.agenzia_entrate_luogo,
    });

    try {
      setLoading(true);
      const response = await axios.post(`${API}/deleghe/generate-pdf`, {
        client_id: selectedClient.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DELEGA_${formData.nome_completo.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      loadDeleghe();
      toast.success('PDF generato con successo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore generazione PDF');
    } finally {
      setLoading(false);
    }
  };

  const deleteDelega = async (delegaId) => {
    try {
      await axios.delete(`${API}/deleghe/${delegaId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDelegaList(prev => prev.filter(d => d.id !== delegaId));
      toast.success('Delega eliminata');
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  const downloadDelegaPdf = async (delega) => {
    try {
      const res = await axios.get(`${API}/deleghe/${delega.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.setAttribute('download', `DELEGA_${delega.nome_completo.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore visualizzazione PDF');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 rounded-lg">
          <FileText className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Delega per Disbrigo Pratiche</h1>
          <p className="text-slate-500">Genera deleghe per i tuoi clienti</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Delega */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Nuova Delega</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Cerca Cliente */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Cerca Cliente *</Label>
                  <Button type="button" size="sm" variant="outline"
                    onClick={() => setNewClientOpen(v => !v)} data-testid="delega-new-client-btn">
                    <UserPlus className="w-4 h-4 mr-1" /> Nuovo cliente
                  </Button>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    className="pl-10"
                    placeholder="Cerca per nome o codice fiscale..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="delega-search"
                  />
                </div>
                {(filteredClients.length > 0 || searchQuery.length >= 2) && (
                  <div className="mt-1 border rounded-md max-h-48 overflow-auto bg-white shadow-lg z-10">
                    {filteredClients.slice(0, 10).map(client => (
                      <div
                        key={client.id}
                        className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                        onClick={() => selectClient(client)}
                        data-testid={`delega-client-option-${client.id}`}
                      >
                        <span className="font-medium">{client.last_name} {client.first_name}</span>
                        {client.fiscal_code && <span className="text-xs text-slate-500 ml-2 font-mono">{client.fiscal_code}</span>}
                      </div>
                    ))}
                    <div className="p-2 text-sm font-medium text-blue-700 hover:bg-blue-50 cursor-pointer flex items-center gap-2"
                      onClick={() => setNewClientOpen(true)} data-testid="delega-create-new-client">
                      <UserPlus className="w-4 h-4" /> Crea nuovo cliente{searchQuery ? `: "${searchQuery}"` : ''}
                    </div>
                  </div>
                )}

                {newClientOpen && (
                  <div className="mt-2">
                    <NewClientForm
                      prefillName={searchQuery}
                      onCancel={() => setNewClientOpen(false)}
                      onCreated={(created) => {
                        setClients(prev => [created, ...prev]);
                        setNewClientOpen(false);
                        setSearchQuery('');
                        selectClient(created);
                      }}
                    />
                  </div>
                )}

                {selectedClient && (
                  <Badge className="mt-2 bg-blue-100 text-blue-700">
                    {selectedClient.last_name} {selectedClient.first_name}
                  </Badge>
                )}
              </div>

              {/* Delega generale del cliente (tutti i servizi, fino a revoca) — sotto resta la delega specifica */}
              {selectedClient && <DelegaGeneraleCliente client={selectedClient} />}

              {/* IL DELEGANTE (dati dal cliente selezionato) */}
              {selectedClient && (
                <div className="p-4 bg-slate-50 rounded-lg text-sm">
                  <h3 className="font-semibold text-slate-700 flex items-center gap-2 mb-2">
                    <User className="w-4 h-4" /> IL DELEGANTE
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 text-slate-700">
                    <div><span className="text-slate-500">Sottoscritto/a:</span> <span className="font-medium">{formData.nome_completo || '—'}</span></div>
                    <div><span className="text-slate-500">Codice Fiscale:</span> <span className="font-mono font-medium">{formData.codice_fiscale || '—'}</span></div>
                    <div><span className="text-slate-500">Nato/a a:</span> {formData.nato_a || '—'} {formData.nato_il ? `il ${formData.nato_il}` : ''}</div>
                    <div><span className="text-slate-500">Residente a:</span> {formData.residente_a || '—'}{formData.via ? `, ${formData.via}` : ''}</div>
                  </div>
                </div>
              )}

              {/* DELEGA - Societa e Delegato */}
              <div className="p-4 bg-blue-50 rounded-lg space-y-4">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> DELEGA
                </h3>
                <div className="text-sm text-slate-600 space-y-1">
                  <p>La societa: <strong>JV SERVICE SRLS</strong></p>
                  <p>P.IVA: <strong>02354210978</strong></p>
                  <p>Sede legale: <strong>Via Fabio Filzi 39L - 59100 Prato (PO)</strong></p>
                  <p className="italic mt-2">nella persona del suo legale rappresentante o di personale da esso incaricato,</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-blue-200">
                  <div>
                    <Label>Nome Delegato</Label>
                    <Input value={formData.delegato_nome} onChange={(e) => setFormData({ ...formData, delegato_nome: e.target.value })} placeholder="Nome della persona incaricata" data-testid="delegato-nome" />
                  </div>
                  <div>
                    <Label>Cognome Delegato</Label>
                    <Input value={formData.delegato_cognome} onChange={(e) => setFormData({ ...formData, delegato_cognome: e.target.value })} placeholder="Cognome della persona incaricata" data-testid="delegato-cognome" />
                  </div>
                </div>
              </div>

              {/* A RAPPRESENTARLO/A PRESSO */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h3 className="font-semibold text-slate-700">A RAPPRESENTARLO/A PRESSO:</h3>
                <div className="space-y-3">
                  {/* Questura */}
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={formData.questura} onChange={(e) => setFormData({ ...formData, questura: e.target.checked })} className="w-4 h-4" data-testid="check-questura" />
                    <Label className="mb-0 w-40">Questura di</Label>
                    <Input value={formData.questura_luogo} onChange={(e) => setFormData({ ...formData, questura_luogo: e.target.value })} className="flex-1" placeholder="Luogo" />
                  </div>
                  {/* Prefettura */}
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={formData.prefettura} onChange={(e) => setFormData({ ...formData, prefettura: e.target.checked })} className="w-4 h-4" data-testid="check-prefettura" />
                    <Label className="mb-0 w-40">Prefettura di</Label>
                    <Input value={formData.prefettura_luogo} onChange={(e) => setFormData({ ...formData, prefettura_luogo: e.target.value })} className="flex-1" placeholder="Luogo" />
                  </div>
                  {/* Comune */}
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={formData.comune} onChange={(e) => setFormData({ ...formData, comune: e.target.checked })} className="w-4 h-4" data-testid="check-comune" />
                    <Label className="mb-0 w-40">Comune di</Label>
                    <Input value={formData.comune_luogo} onChange={(e) => setFormData({ ...formData, comune_luogo: e.target.value })} className="flex-1" placeholder="Luogo" />
                  </div>
                  {/* Agenzia Entrate */}
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={formData.agenzia_entrate} onChange={(e) => setFormData({ ...formData, agenzia_entrate: e.target.checked })} className="w-4 h-4" data-testid="check-agenzia-entrate" />
                    <Label className="mb-0 w-40">Agenzia Entrate di</Label>
                    <Input value={formData.agenzia_entrate_luogo} onChange={(e) => setFormData({ ...formData, agenzia_entrate_luogo: e.target.value })} className="flex-1" placeholder="Luogo" />
                  </div>
                  {/* Altro */}
                  <div className="flex items-center gap-3">
                    <input type="checkbox" checked={formData.altro} onChange={(e) => setFormData({ ...formData, altro: e.target.checked })} className="w-4 h-4" data-testid="check-altro" />
                    <Label className="mb-0 w-40">Altro</Label>
                    <Input value={formData.altro_descrizione} onChange={(e) => setFormData({ ...formData, altro_descrizione: e.target.value })} className="flex-1" placeholder="Specificare" />
                  </div>
                </div>
              </div>

              {/* Operazione */}
              <div>
                <Label>PER LA SEGUENTE OPERAZIONE:</Label>
                <Textarea value={formData.operazione} onChange={(e) => setFormData({ ...formData, operazione: e.target.value })} placeholder="es: consegna documenti, ritiro certificati, presentazione istanza..." rows={3} />
              </div>

              {/* Luogo e Data */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Luogo e Data</Label>
                  <Input value={formData.luogo_data} onChange={(e) => setFormData({ ...formData, luogo_data: e.target.value })} placeholder="Prato, 28/03/2026" />
                </div>
              </div>

              {/* Azioni */}
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={generatePDF} disabled={loading || !selectedClient} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="delega-download-pdf">
                  <Download className="w-4 h-4 mr-2" />
                  {loading ? 'Generazione...' : 'Genera e Scarica PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Deleghe Recenti */}
        <div className="space-y-6">
          <ElencoDelegheGenerali />
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                Deleghe Recenti
                <Badge variant="outline">{delegaList.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {delegaList.length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">Nessuna delega generata</p>
              ) : (
                <div className="divide-y max-h-[600px] overflow-auto">
                  {delegaList.slice((delegaPage - 1) * DELEGA_PER_PAGE, delegaPage * DELEGA_PER_PAGE).map(delega => (
                    <div key={delega.id} className="px-4 py-3 hover:bg-slate-50 group">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{delega.nome_completo}</p>
                          <p className="text-xs text-slate-500 font-mono">{delega.codice_fiscale}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{formatDate(delega.created_at)}</p>
                        </div>
                        <div className="flex items-center gap-1 ml-2 shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => downloadDelegaPdf(delega)} data-testid={`delega-view-pdf-${delega.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => deleteDelega(delega.id)} data-testid={`delega-delete-${delega.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {delegaList.length > DELEGA_PER_PAGE && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-xs text-slate-500">
                    Pagina {delegaPage} di {Math.ceil(delegaList.length / DELEGA_PER_PAGE)}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setDelegaPage(p => Math.max(1, p - 1))} disabled={delegaPage === 1}>
                      Precedente
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDelegaPage(p => Math.min(Math.ceil(delegaList.length / DELEGA_PER_PAGE), p + 1))}
                      disabled={delegaPage >= Math.ceil(delegaList.length / DELEGA_PER_PAGE)}
                    >
                      Successiva
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Delega;
